import React, { useContext, useState } from 'react';
import '../../../App.css';
import './active-session-panel.css';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '../../../app/hooks';

// types
import { CanfarServiceType, ComputeServiceType, Site } from '../../../utils/types';
import { Event, EventType, EventTarget } from '../../../utils/events';

// images
import renewIcon from '../../../icons/refresh.512.png';
import binLightIcon from '../../../icons/bin-dark.256.png';
import refreshIcon from '../../../icons/refresh.512.png';
import addIcon from '../../../icons/plus.256.png';

// functions
import { APIPrefix, siteName } from '../../../utils/functions';

// classes
import ComputeSession from '../../compute-tiles/compute-session';
import PleaseWait from '../../../tools/please-wait';
import Scrollbox from '../../../tools/scrollbox';
import SiteIcon from '../../../tools/site-icon';

//	--------------------------------------------------------------------------
//
//	R E D U X   S T A T E   M A N A G E M E N T
//
//	--------------------------------------------------------------------------

// import state definition and functions.
import {
  refreshSRCNetServices,
  refreshCanfarServices,
} from '../../interactive-sessions/interactive-sessions-actions';
import { setShowNewSessionModal } from '../../../app-slice';
import { handleEvent } from '../../../app-actions';

//	--------------------------------------------------------------------------
//
//	C L A S S   D E F I N I T I O N
//
//	--------------------------------------------------------------------------

const ActiveSessionsPanel: React.FC = () => {
  //
  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  // hold the list of compute services from the site-capabilities catalog.
  const sSessions: ComputeServiceType[] = useAppSelector(
    (state) => state.interactiveSessions.sessions,
  );
  const sSessionsLoading: {
    resource_type: string;
    compute_status: string;
    id?: string;
  }[] = useAppSelector((state) => state.interactiveSessions.sessionsLoading);

  // global state.
  const sG_darkMode: boolean = useAppSelector((state) => state.global.darkMode);
  const sG_loggedIn: boolean | undefined = useAppSelector(
    (state) => state.global.loggedIn,
  );

  // data state.
  const sD_canfarServices: CanfarServiceType[] = useAppSelector(
    (state) => state.data.canfarServices ?? [],
  );

  //	--------------------------------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  // translation function
  const { t } = useTranslation();
  const [loadingNotebook, setLoadingNotebook] = useState(false);

  // extract the list of active canfar sessions that are loading.
  const activeSessionsCanfarLoading: {
    resource_type: string;
    compute_status: string;
    id?: string;
  }[] = sSessionsLoading.filter(
    (item) => item.resource_type === 'canfar' && item.compute_status === 'running',
  );

  // determine if the active SRCNet sessions are loading.
  const activeSessionsSRCNetLoading: boolean =
    sSessionsLoading.findIndex(
      (item) => item.resource_type === 'srcnet' && item.compute_status === 'running',
    ) > -1;

  // get a list of site uuids from the current list of active services.
  let allSites: Site[] = [];
  for (let service of sSessions) {
    if (
      (['running', 'pending'].indexOf(service.compute_status) > -1 ||
        ['starting', 'renewing', 'removing'].indexOf(service.overridden_status ?? '/') >
          -1) &&
      allSites.findIndex((site) => site.site_uuid === service.site.site_uuid) === -1
    ) {
      allSites.push(service.site);
    }
  }

  //	------------------------------------------------------------
  //
  //	Add a service to a particular site.
  //
  //	------------------------------------------------------------

  function addService(args: { site_uuid?: string }) {
    //
    dispatch(setShowNewSessionModal({ show: true, site_uuid: args.site_uuid }));
    //
  } // addService

  const handleLaunchNotebook = async (url: string) => {
    console.log('Launching in the Notebook tab>>>>>>>>');
    console.log('loading state: ' + loadingNotebook);
    const newEvent: Event = {
      eventTarget: EventTarget.APP,
      eventType: EventType.LAUNCH_NOTEBOOK,
      parameters: url,
    };
    dispatch(handleEvent({ event: newEvent }));
  };

  //	------------------------------------------------------------
  //
  //	Refresh the services for a particular site.
  //
  //	------------------------------------------------------------

  function refreshServices(args: { site_uuid?: string }) {
    //
    // get the canfar instance for this site.
    let canfarService: CanfarServiceType | undefined = undefined;

    if (args.site_uuid !== undefined) {
      canfarService = sD_canfarServices.find(
        (service) => service.site.site_uuid === args.site_uuid,
      );
    }

    // refresh services.
    if (canfarService !== undefined) {
      dispatch(refreshCanfarServices({ canfarService: canfarService }));
    }
    dispatch(refreshSRCNetServices());
    //
  } // refreshServices

  const hasNoSessions = allSites.length === 0;

  //	------------------------------------------------------------
  //
  //	C O M P O N E N T   C O D E
  //
  //	------------------------------------------------------------

  return (
    <>
      <Scrollbox
        id="active-sessions-panel-scrollbox"
        direction="vertical"
        width="100%"
        flex="1 1"
        flexDirection="column"
      >
        <div className="active-sessions-container" style={{ flex: '1 1' }}>
          {hasNoSessions === true && sG_loggedIn === true && (
            <div className="no-data-overlay">
              <span>No active sessions</span>
            </div>
          )}
          {sG_loggedIn !== true && (
            <div className="no-data-overlay">
              <span>Login required</span>
            </div>
          )}
          {hasNoSessions !== true &&
            sG_loggedIn === true &&
            allSites
              .sort((a, b) => (siteName({ site: a }) > siteName({ site: b }) ? 1 : -1))
              .map((site) => {
                return (
                  <div key={site.site_uuid} className="src-group">
                    <div
                      role="heading"
                      aria-level={3}
                      className="src-header text-colour"
                      style={{
                        borderTop: sG_darkMode === true ? 'none' : '1pt solid #eaeaea',
                        borderBottom: sG_darkMode === true ? 'none' : '1pt solid #eaeaea',
                        display: 'flex',
                        flexDirection: 'row',
                      }}
                    >
                      <SiteIcon site={site} size={30} />
                      <div style={{ flex: '0 0 10px' }} />
                      <div style={{ flex: '1 1' }}>{siteName({ site: site })}</div>
                      <button
                        id="refresh-site"
                        className="mini-button"
                        type="button"
                        style={{ flex: '0 0 auto' }}
                        onClick={(e) => refreshServices({ site_uuid: site.site_uuid })}
                      >
                        <img
                          src={refreshIcon}
                          alt="Refresh site"
                          width="34"
                          height="34"
                          title={t('Refresh')}
                        />
                      </button>
                      <div style={{ flex: '0 0 10px' }} />
                      <button
                        id="add-service-to-site"
                        className="mini-button"
                        type="button"
                        style={{ flex: '0 0 auto' }}
                        onClick={(e) => addService({ site_uuid: site.site_uuid })}
                      >
                        <img
                          src={addIcon}
                          alt="Add service"
                          width="34"
                          height="34"
                          title={t('Add service')}
                        />
                      </button>
                    </div>

                    <div style={{ paddingRight: '5px' }}>
                      <div className="session-card-overlay">
                        <div className="session-card-row">
                          {sSessions
                            .filter(
                              (session) =>
                                session.site.site_uuid === site.site_uuid &&
                                (['running', 'pending'].indexOf(session.compute_status) >
                                  -1 ||
                                  ['starting', 'renewing', 'removing'].indexOf(
                                    session.overridden_status ?? '/',
                                  ) > -1),
                            )
                            .sort((a, b) => (a.service_name > b.service_name ? 1 : -1))
                            .map((session: ComputeServiceType) => {
                              return (
                                <>
                                  {loadingNotebook && (
                                    <div className="session-spinner-overlay">
                                      <div className="spinner" />
                                    </div>
                                  )}
                                  <ComputeSession
                                    key={
                                      site.site_uuid +
                                      '_' +
                                      (session.id?.toString() ?? '')
                                    }
                                    session={session}
                                  />
                                </>
                              );
                            })}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
          <div style={{ flex: '1 1' }} />
        </div>
        {/*</div>*/}
      </Scrollbox>
      {activeSessionsSRCNetLoading === true || activeSessionsCanfarLoading.length > 0 ? (
        <div style={{ marginLeft: '15px', flex: '0 0 auto' }}>
          <PleaseWait
            text={
              activeSessionsCanfarLoading.length > 0
                ? 'Checking services.....' +
                  activeSessionsCanfarLoading.length.toString() +
                  ' site(s) remaining'
                : 'Checking services'
            }
            visible={true}
          />
        </div>
      ) : (
        <></>
      )}
    </>
  );
};

export default ActiveSessionsPanel;
